import React, { Component } from 'react';
import { any, array, arrayOf, bool, number, object, oneOfType, shape, string } from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { getCustomCSSPropertiesFromConfig } from '../../util/style';
import { useIntl, intlShape } from '../../util/reactIntl';
import { metaTagProps } from '../../util/seo';
import { canonicalRoutePath } from '../../util/routes';
import { buildSiteSchemas, isHomeCanonicalPath } from '../../util/siteStructuredData';
import { propTypes } from '../../util/types';

import css from './Page.module.css';

const preventDefault = e => {
  e.preventDefault();
};

// The site is written for Switzerland: German pages are de-CH (Swiss spelling, CHF). intl.locale
// itself stays 'de' — number and date formatting and the hydration of every FormattedMessage
// depend on it — only the language the document declares changes.
export const documentLanguage = locale => (locale === 'de' ? 'de-CH' : locale);

const webmanifestURL = () => {
  return '/site.webmanifest';
};

const getFaviconVariants = config => {
  // We add favicon through hosted configs
  // NOTE: There's no favicon.ico file. This is an imageAsset object which is used together with <meta> tags.
  const favicon = config.branding.favicon;
  return favicon?.type === 'imageAsset' ? Object.values(favicon.attributes.variants) : [];
};

// Identifies the page a scroll position belongs to. A modal can also be closed by navigating away
// (a link inside the mobile menu), and the route decides the scroll position of the page it
// navigates to — restoring a position captured on the previous page would drop the visitor into
// the middle of a page they have never scrolled.
// Note: the query string is deliberately left out. Modals that keep their open state in the URL
// change it on the way in and out, and the visitor is still on the same page.
const locationSignature = location => `${location?.pathname || ''}${location?.hash || ''}`;

const getAppleTouchIconURL = config => {
  // The appIcon is used to pick apple-touch-icon
  // We use 180x180. I.e. we follow the example set by realfavicongenerator
  const appIcon = config.branding.appIcon;
  const appIconVariants =
    appIcon?.type === 'imageAsset' ? Object.values(appIcon.attributes.variants) : [];
  const appleTouchIconVariant = appIconVariants.find(variant => {
    return variant.width === 180 && variant.height === 180;
  });
  return appleTouchIconVariant?.url;
};

class PageComponent extends Component {
  constructor(props) {
    super(props);
    // Keeping scrollPosition out of state reduces rendering cycles (and no bad states rendered)
    this.scrollPosition = 0;
    this.scrollPositionLocation = null;
    this.scrollingDisabledChanged = this.scrollingDisabledChanged.bind(this);
  }

  componentDidMount() {
    // By default a dropped file is loaded in the browser window as a
    // file URL. We want to prevent this since it might loose a lot of
    // data the user has typed but not yet saved. Preventing requires
    // handling both dragover and drop events.
    document.addEventListener('dragover', preventDefault);
    document.addEventListener('drop', preventDefault);

    // Remove duplicated server-side rendered page schema.
    // It's in <body> to improve initial rendering performance,
    // but after web app is initialized, react-helmet-async operates with <head>
    const pageSchema = document.getElementById('page-schema');
    if (pageSchema) {
      pageSchema.remove();
    }
  }

  componentDidUpdate(prevProps) {
    // The last modal just closed. This is a lifecycle and not a side effect in render(), because
    // render used to decide whether to restore by reading the inline marginTop back out of the
    // committed DOM. That read is only reliable when the flip to scrollingDisabled=false is the
    // only update in the batch: closing a modal whose open state lives in the URL also updates the
    // router, and in that render the style had already been cleared, so nothing was restored.
    if (prevProps.scrollingDisabled && !this.props.scrollingDisabled) {
      this.restoreScrollPosition();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('dragover', preventDefault);
    document.removeEventListener('drop', preventDefault);
  }

  // Captures the scroll position at the moment scrolling gets disabled (i.e. a modal opens).
  //
  // This has to stay in the render phase: the commit that follows adds .scrollingDisabled, which
  // makes the root `position: fixed` and collapses the document to a single viewport, and from
  // then on window.pageYOffset reads 0. The same render also needs the value for the negative
  // margin that keeps the content visually in place, so getSnapshotBeforeUpdate would be too late.
  scrollingDisabledChanged(currentScrollingDisabled) {
    if (currentScrollingDisabled === this.scrollingDisabled) {
      return;
    }
    if (currentScrollingDisabled) {
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      this.scrollPositionLocation = locationSignature(this.props.location);
    }
    this.scrollingDisabled = currentScrollingDisabled;
  }

  restoreScrollPosition() {
    if (locationSignature(this.props.location) !== this.scrollPositionLocation) {
      // The modal was closed by navigating somewhere else. That page has its own scroll position.
      return;
    }
    const scrollPosition = this.scrollPosition;

    window.requestAnimationFrame(() => {
      // While .scrollingDisabled was applied, the root was `position: fixed; height: 100vh` and the
      // document was collapsed to one viewport. The class is gone by now, but rAF callbacks run
      // before the frame's style and layout pass — reading a layout property forces that work to
      // happen first, otherwise the scroll is clamped to the collapsed document height, i.e. to 0.
      const maxScrollTop = Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
      window.scrollTo(0, Math.min(scrollPosition, maxScrollTop));
    });
  }

  render() {
    const {
      className,
      rootClassName,
      children,
      location,
      intl,
      scrollingDisabled,
      referrer,
      author,
      openGraphType = 'website',
      description,
      facebookImages,
      published,
      schema,
      socialSharing,
      title,
      twitterHandle,
      twitterImages,
      updated,
      noIndex,
      config,
      routeConfiguration,
    } = this.props;

    // The search pill sits in the desktop topbar: a taller bar (Page.module.css).
    const classes = classNames(rootClassName || css.root, css.unifiedSearchHeader, className, {
      [css.scrollingDisabled]: scrollingDisabled,
    });

    this.scrollingDisabledChanged(scrollingDisabled);

    const marketplaceRootURL = config.marketplaceRootURL;
    const shouldReturnPathOnly = referrer && referrer !== 'unsafe-url';
    const canonicalPath = canonicalRoutePath(routeConfiguration, location, shouldReturnPathOnly);
    const canonicalUrl = `${marketplaceRootURL}${canonicalPath}`;

    const marketplaceName = config.marketplaceName;
    const schemaTitle = intl.formatMessage({ id: 'Page.schemaTitle' }, { marketplaceName });
    // Formatted only when needed: pages that bring their own description should not pay for (or
    // log a missing-translation warning about) the fallback. The old fallback,
    // 'Page.schemaDescription', reads 'Ein Online-Marktplatz.' and can be overridden in the
    // Console; the new key is repo-only on purpose, so the repo decides what it says.
    const defaultDescription = () => intl.formatMessage({ id: 'Page.seoDefaultDescription' });
    const pageTitle = title || schemaTitle;
    const pageDescription = description || defaultDescription();
    const htmlLang = documentLanguage(intl.locale);
    const {
      title: socialSharingTitle,
      description: socialSharingDescription,
      images1200: socialSharingImages1200,
      // Note: we use image with open graph's aspect ratio (1.91:1) also with Twitter
      images600: socialSharingImages600,
    } = socialSharing || {};

    // Images for social media sharing
    const defaultFacebookImageURL = config.branding.facebookImage;
    const openGraphFallbackImages = [
      {
        name: 'facebook',
        url: defaultFacebookImageURL,
        width: 1200,
        height: 630,
      },
    ];
    const defaultTwitterImageURL = config.branding.twitterImage;
    const twitterFallbackImages = [
      {
        name: 'twitter',
        url: defaultTwitterImageURL,
        width: 600,
        height: 314,
      },
    ];
    const facebookImgs = socialSharingImages1200 || facebookImages || openGraphFallbackImages;
    const twitterImgs = socialSharingImages600 || twitterImages || twitterFallbackImages;

    const metaToHead = metaTagProps(
      {
        author,
        openGraphType,
        socialSharingTitle: socialSharingTitle || pageTitle,
        socialSharingDescription: socialSharingDescription || pageDescription,
        description: pageDescription,
        facebookImages: facebookImgs,
        twitterImages: twitterImgs,
        twitterHandle,
        published,
        updated,
        url: canonicalUrl,
        locale: intl.locale,
        noIndex,
      },
      config
    );

    // Schema for search engines (helps them to understand what this page is about)
    // https://schema.org — JSON-LD format
    //
    // Schema attribute can be either single schema object or an array of objects
    // This makes it possible to include several different items from the same page.
    // E.g. Product, Place, Video
    //
    // The Organization and the WebSite are described once, on the home page (see
    // util/siteStructuredData.js). Other pages refer to them by @id in their own schema.
    const hasSchema = schema != null;
    const schemaFromProps = hasSchema && Array.isArray(schema) ? schema : hasSchema ? [schema] : [];
    const siteSchemas = isHomeCanonicalPath(canonicalPath)
      ? buildSiteSchemas(config, {
          organizationDescription: intl.formatMessage({ id: 'Page.seoOrganizationDescription' }),
          websiteDescription: defaultDescription(),
        })
      : [];
    const schemaGraph = [...schemaFromProps, ...siteSchemas];
    const schemaArrayJSONString =
      schemaGraph.length > 0
        ? JSON.stringify({ '@context': 'https://schema.org', '@graph': schemaGraph })
        : null;

    const scrollPositionStyles = scrollingDisabled
      ? { marginTop: `${-1 * this.scrollPosition}px` }
      : {};

    const faviconVariants = getFaviconVariants(config);
    const appleTouchIcon = getAppleTouchIconURL(config);

    // Marketplace color and the color for <PrimaryButton> come from configs
    // If set, we need to create those custom CSS Properties and set them for the app
    // Note: this is also set to <html> element in app.js to provide marketplace colors for modals/portals.
    const styles = getCustomCSSPropertiesFromConfig(config.branding);

    return (
      <div id="page" className={classes} style={styles}>
        <Helmet
          htmlAttributes={{
            lang: htmlLang,
          }}
        >
          <title>{pageTitle}</title>
          {referrer ? <meta name="referrer" content={referrer} /> : null}
          <link rel="canonical" href={canonicalUrl} />
          <link rel="dns-prefetch" href="//assets-sharetribecom.sharetribe.com" />
          <link rel="dns-prefetch" href="//sharetribe.imgix.net" />
          <link
            rel="preconnect"
            href="https://assets-sharetribecom.sharetribe.com"
            crossOrigin=""
          />
          <link rel="preconnect" href="https://sharetribe.imgix.net" crossOrigin="" />

          {faviconVariants.map(variant => {
            return (
              <link
                key={`icon_${variant.width}`}
                rel="icon"
                type="image/png"
                sizes={`${variant.width}x${variant.height}`}
                href={variant.url}
              />
            );
          })}

          {appleTouchIcon ? (
            <link rel="apple-touch-icon" sizes="180x180" href={appleTouchIcon} />
          ) : null}

          <link rel="manifest" href={webmanifestURL()} />

          <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8" />
          <meta httpEquiv="Content-Language" content={htmlLang} />
          {metaToHead.map((metaProps, i) => (
            <meta key={i} {...metaProps} />
          ))}
          {schemaArrayJSONString ? (
            <script id="page-schema" type="application/ld+json">
              {schemaArrayJSONString.replace(/</g, '\\u003c')}
            </script>
          ) : null}
        </Helmet>
        <div className={css.content} style={scrollPositionStyles}>
          {children}
        </div>
      </div>
    );
  }
}

/**
 * @typedef {Object} ImageConfig
 * @property {string} width - The width of the image
 * @property {string} height - The height of the image
 * @property {string} url - The URL of the image
 */
/**
 * @typedef {Object} SocialSharingConfig
 * @property {string} title - The title for the social sharing service
 * @property {string} description - The description for the social sharing service
 * @property {Array<ImageConfig>} images1200 - The images for social sharing (1200x630)
 * @property {Array<ImageConfig>} images600 - The images for social sharing (600x314)
 */
/**
 * A component that renders a page.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {ReactNode} props.children - The children to render
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {string} props.referrer - Handle referrer policy
 * @param {string} props.author - The author
 * @param {string} props.openGraphType - The open graph type (aka 'og:type')
 * @param {string} props.description - The description
 * @param {Array<ImageConfig>} props.facebookImages - The facebook images
 * @param {Array<ImageConfig>} props.twitterImages - The twitter images
 * @param {string} props.published - The published date (article:published_time)
 * @param {object|array} props.schema - The schema from
 * @param {SocialSharingConfig} props.socialSharing - The social sharing
 * @param {string} props.title - The page title
 * @param {string} props.twitterHandle - The twitter handle
 * @param {string} props.updated - The updated date article:modified_time
 * @returns {JSX.Element} Page component that handles SEO and social sharing
 */
const Page = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const location = useLocation();
  const intl = useIntl();

  return (
    <PageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      location={location}
      intl={intl}
      {...props}
    />
  );
};

export default Page;
